"use client";

import { useCallback, useMemo, useRef } from "react";
import type { TouchEventHandler } from "react";
import { useSwipeTabPanelTransition } from "./useSwipeTabPanelTransition";
import type {
  SwipeTabDirection,
  SwipeTabPanelTransitionMode,
} from "./useSwipeTabPanelTransition";

export const HORIZONTAL_SCROLL_ATTR = "data-horizontal-scroll";

export type SwipeTabItem<T extends string> = {
  id: T;
};

type UseSwipeTabsParams<T extends string> = {
  /** 按显示顺序排列的 tab 列表 */
  tabs: SwipeTabItem<T>[];
  /** 当前激活 tab id */
  activeId: T | undefined;
  /** 切换 tab 的统一回调（内部只负责算 next，不负责业务状态） */
  onChange: (id: T) => void;
  /** 手势功能开关，建议由调用方基于设备类型控制 */
  enabled: boolean;
  /** 最小水平滑动阈值（px） */
  threshold?: number;
  /**
   * 屏幕边缘保护距离（px）。
   * 在左右边缘起手时让出手势，降低与系统返回手势冲突的概率。
   */
  edgeGuardSize?: number;
  /** 自定义“横向滚动区域”判定，用于手势冲突规避 */
  isScrollableElement?: (element: HTMLElement | null) => boolean;
  /**
   * 可选面板入场动画配置。
   *
   * 不传时：useSwipeTabs 只负责手势识别与切换。
   * 传入时：useSwipeTabs 会在“滑动触发 onChange 前”自动准备动画方向，并返回 transitionClassName。
   * （点击 tab 默认不触发动画；如需点击也触发，可显式调用返回的 changeWithTransition）
   *
   * 注意：
   * - transitionClassName 应挂到“纯视觉内层容器”；
   * - 不要挂到滚动容器、高度记录容器、ScrollRestorer 观察容器上；
   * - class 内部使用 transform，直接作用在布局/滚动容器上可能影响滚动高度记忆。
   */
  transition?: {
    /** 动画开关，通常与 enabled 保持一致 */
    enabled: boolean;
    /** route 用于路由型 tab；component 用于同页组件切换 tab */
    mode?: SwipeTabPanelTransitionMode;
    /** route 模式必填：页面级唯一 key，用于跨路由传递方向 */
    storageKey?: string;
    /** 动画时长，需与全局 CSS 动画时长保持一致 */
    animationMs?: number;
  };
};

type SwipeBind = {
  /** 手势起点：记录初始坐标与冲突状态 */
  onTouchStart: TouchEventHandler<HTMLElement>;
  /** 手势过程：更新终点坐标（用于计算滑动向量） */
  onTouchMove: TouchEventHandler<HTMLElement>;
  /** 手势结束：判断是否满足切 tab 条件并触发 onChange */
  onTouchEnd: TouchEventHandler<HTMLElement>;
};

/** 默认最小水平滑动距离，过小会造成误触。 */
const DEFAULT_THRESHOLD = 40;
/** 默认系统边缘手势保护宽度（左右各 24px）。 */
const DEFAULT_EDGE_GUARD_SIZE = 24;

/**
 * 默认冲突判定：
 * 起始触点命中 data-horizontal-scroll 区域时，整次手势让出给内部横向滚动列表。
 */
const defaultIsScrollableElement = (element: HTMLElement | null): boolean => {
  return !!element?.closest(`[${HORIZONTAL_SCROLL_ATTR}]`);
};

/**
 * H5 左右滑切换 tab 的通用手势 hook。
 *
 * 适用前提：
 * - 页面存在“有序 tab 列表 + 当前 active tab + 统一 onChange”；
 * - 期望在主内容区支持横向滑动切换，而不是仅靠点击 tab。
 *
 * 设计要点：
 * 1) 仅识别“横向主导 + 超过阈值”的手势，避免与页面纵向滚动冲突。
 * 2) touchstart 命中横向滚动区时，当前手势不触发 tab 切换。
 * 3) touchstart 命中屏幕边缘保护区时，让出给系统级返回手势。
 * 4) 仅计算并触发 next tab，不耦合页面布局或业务数据。
 * 5) 可选内置面板入场动画：传入 transition 后，hook 会在切换前准备动画方向。
 *
 * 返回值：
 * - `bind`：可直接展开到容器上的 touch 事件集合。
 * - `transitionClassName`：可选动画 class，必须挂到纯视觉内层容器，避免影响滚动/高度容器。
 * - `changeWithTransition`：带动画的切换入口；仅当点击 tab 也需要动画时使用。
 */
export const useSwipeTabs = <T extends string>({
  tabs,
  activeId,
  onChange,
  enabled,
  threshold = DEFAULT_THRESHOLD,
  edgeGuardSize = DEFAULT_EDGE_GUARD_SIZE,
  isScrollableElement,
  transition,
}: UseSwipeTabsParams<T>): {
  bind: SwipeBind;
  transitionClassName: string | undefined;
  transitionDirection: SwipeTabDirection | undefined;
  changeWithTransition: (id: T) => void;
  prepareTransition: (id: T) => SwipeTabDirection | undefined;
} => {
  const { transitionClassName, transitionDirection, prepareTransition } =
    useSwipeTabPanelTransition<T>({
      tabs,
      activeId,
      transitionMode: transition?.mode,
      storageKey: transition?.storageKey,
      enabled: enabled && !!transition?.enabled,
      animationMs: transition?.animationMs,
    });

  // 起点坐标：来自 touchstart
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  // 终点坐标：在 touchmove 中持续更新，touchend 时用于计算位移
  const endXRef = useRef(0);
  const endYRef = useRef(0);
  // 当前手势是否应忽略（命中横向滚动区或边缘保护区时置为 true）
  const ignoreSwipeRef = useRef(false);

  const changeWithTransition = useCallback(
    (id: T) => {
      if (id === activeId) {
        return;
      }
      // 若调用方启用了动画，这里会在业务切换前记录方向；未启用时 prepareTransition 为 no-op。
      prepareTransition(id);
      onChange(id);
    },
    [activeId, onChange, prepareTransition],
  );

  const bind = useMemo<SwipeBind>(() => {
    const onTouchStart: TouchEventHandler<HTMLElement> = (event) => {
      // 开关关闭时完全不处理，避免非 H5 场景误触发。
      if (!enabled) {
        return;
      }

      const target = event.target as HTMLElement | null;
      // 只对“当前这一次手势”做冲突判定，不会全局禁用页面切 tab。
      const shouldIgnore =
        isScrollableElement?.(target) ?? defaultIsScrollableElement(target);
      ignoreSwipeRef.current = shouldIgnore;

      const touch = event.touches[0];
      if (!touch) {
        return;
      }
      const viewportWidth = window.innerWidth || 0;
      // 边缘起手优先让给系统返回手势（iOS/Android）。
      const isEdgeGesture =
        touch.clientX <= edgeGuardSize ||
        (viewportWidth > 0 && touch.clientX >= viewportWidth - edgeGuardSize);
      if (isEdgeGesture) {
        ignoreSwipeRef.current = true;
      }
      // 每次手势都重置起点/终点，避免复用上一手势残留数据。
      startXRef.current = touch.clientX;
      startYRef.current = touch.clientY;
      endXRef.current = touch.clientX;
      endYRef.current = touch.clientY;
    };

    const onTouchMove: TouchEventHandler<HTMLElement> = (event) => {
      // 冲突场景直接跳过，避免无意义计算。
      if (!enabled || ignoreSwipeRef.current) {
        return;
      }
      const touch = event.touches[0];
      if (!touch) {
        return;
      }
      endXRef.current = touch.clientX;
      endYRef.current = touch.clientY;
    };

    const onTouchEnd: TouchEventHandler<HTMLElement> = () => {
      // 任一前置条件不满足都直接收尾，并清理 ignore 状态。
      if (!enabled || ignoreSwipeRef.current || !activeId) {
        ignoreSwipeRef.current = false;
        return;
      }

      // activeId 可能已不在 tabs 中（数据异步更新场景），此时不切换。
      const currentIndex = tabs.findIndex((tab) => tab.id === activeId);
      if (currentIndex < 0) {
        ignoreSwipeRef.current = false;
        return;
      }

      const deltaX = endXRef.current - startXRef.current;
      const deltaY = endYRef.current - startYRef.current;
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      // 仅在横向位移占优且达到阈值时触发切换。
      if (absX < threshold || absX <= absY) {
        ignoreSwipeRef.current = false;
        return;
      }

      // 方向约定：左滑 -> 下一个；右滑 -> 上一个。
      const nextIndex = deltaX < 0 ? currentIndex + 1 : currentIndex - 1;
      // 边界保护：首尾 tab 不越界切换。
      if (nextIndex < 0 || nextIndex >= tabs.length) {
        ignoreSwipeRef.current = false;
        return;
      }

      // 滑动切换始终走带动画入口；点击 tab 是否使用动画由调用方自行决定。
      changeWithTransition(tabs[nextIndex].id);
      ignoreSwipeRef.current = false;
    };

    return { onTouchStart, onTouchMove, onTouchEnd };
  }, [
    activeId,
    edgeGuardSize,
    enabled,
    isScrollableElement,
    changeWithTransition,
    tabs,
    threshold,
  ]);

  return {
    bind,
    transitionClassName,
    transitionDirection,
    changeWithTransition,
    prepareTransition,
  };
};
